import { v4 as uuid } from 'uuid'

import { EchoConnection, EchoPlugin } from '../types'

// EchoClient will be imported from data-access when we move it there
interface EchoClientLike {
  logger: {
    error(...args: unknown[]): void
  }
}

export type UserJourneyEventType =
  | { type: 'start' }
  | { type: 'tag' }
  | { type: 'variant' }
  | { type: 'frustrationSignal' }
  | { type: 'frictionSignal' }
  | { type: 'end'; outcome: string }
  | { type: 'log' }
  | { type: 'updateActiveJourney'; activeJourneysData?: string }
  | { type: 'report'; requestData: string }

export interface UserJourneyEvent {
  id: string
  timestamp: number
  journeyName: string
  eventType: UserJourneyEventType
  eventValue: string
  metadata?: Record<string, string>
}

/**
 * UserJourneys Plugin for Echo
 * Tracks and reports user journey events to Echo desktop
 */
export class UserJourneysPlugin implements EchoPlugin {
  readonly id = 'com.block.plugin.userjourneys'
  private connection: EchoConnection | null = null
  private echoClient: EchoClientLike

  constructor(echoClient: EchoClientLike) {
    this.echoClient = echoClient
  }

  onConnect(connection: EchoConnection): void {
    this.connection = connection
  }

  onDisconnect(): void {
    this.connection = null
  }

  onDesktopPluginActive(): void {
    // Plugin became active
  }

  onDesktopPluginInactive(): void {
    // Plugin became inactive
  }

  onDataReceived(): void {
    // Handle data from desktop if needed
  }

  /**
   * Send a journey event to Echo
   */
  private sendJourneyEvent(event: UserJourneyEvent): void {
    if (!this.connection) return

    try {
      this.connection.send(JSON.stringify(event))
    } catch (error) {
      this.echoClient.logger.error('[UserJourneysPlugin] Failed to send event:', error)
    }
  }

  /**
   * Notifies that a journey has started
   */
  didStartJourney(
    journeyId: string,
    journeyTimestamp: number,
    userJourneyName: string,
    timeout: number,
    metadata?: Record<string, string>
  ): void {
    this.sendJourneyEvent({
      id: journeyId,
      timestamp: journeyTimestamp,
      journeyName: userJourneyName,
      eventType: { type: 'start' },
      eventValue: userJourneyName,
      metadata: {
        ...(metadata || {}),
        timeout: timeout.toString(),
      },
    })
  }

  /**
   * Adds a variant to the journey
   */
  didAddVariant(journeyId: string, userJourneyName: string, variant: string, metadata?: Record<string, string>): void {
    this.sendJourneyEvent({
      id: uuid(),
      timestamp: this.getTimestamp(),
      journeyName: userJourneyName,
      eventType: { type: 'variant' },
      eventValue: variant,
      metadata: { ...metadata, journeyId },
    })
  }

  /**
   * Adds a frustration signal to the journey
   */
  didAddFrustrationSignal(
    journeyId: string,
    userJourneyName: string,
    frustrationSignal: string,
    metadata?: Record<string, string>
  ): void {
    this.sendJourneyEvent({
      id: uuid(),
      timestamp: this.getTimestamp(),
      journeyName: userJourneyName,
      eventType: { type: 'frustrationSignal' },
      eventValue: frustrationSignal,
      metadata: { ...metadata, journeyId },
    })
  }

  /**
   * Adds a friction signal to the journey
   */
  didAddFrictionSignal(
    journeyId: string,
    userJourneyName: string,
    frictionSignal: string,
    metadata?: Record<string, string>
  ): void {
    this.sendJourneyEvent({
      id: uuid(),
      timestamp: this.getTimestamp(),
      journeyName: userJourneyName,
      eventType: { type: 'frictionSignal' },
      eventValue: frictionSignal,
      metadata: { ...metadata, journeyId },
    })
  }

  /**
   * Adds a tag to the journey
   */
  didAddTag(journeyId: string, userJourneyName: string, tag: string, metadata?: Record<string, string>): void {
    this.sendJourneyEvent({
      id: uuid(),
      timestamp: this.getTimestamp(),
      journeyName: userJourneyName,
      eventType: { type: 'tag' },
      eventValue: tag,
      metadata: { ...metadata, journeyId },
    })
  }

  /**
   * Notifies that a journey has ended
   */
  didEndJourney(journeyId: string, userJourneyName: string, outcome: string, metadata?: Record<string, string>): void {
    this.sendJourneyEvent({
      id: uuid(),
      timestamp: this.getTimestamp(),
      journeyName: userJourneyName,
      eventType: { type: 'end', outcome },
      eventValue: userJourneyName,
      metadata: { ...metadata, journeyId },
    })
  }

  /**
   * Updates active journeys UI
   */
  didUpdateActiveJourneys(
    activeJourneysJSONRepresentation: string,
    currentActiveJourneyName: string | null,
    metadata?: Record<string, string>
  ): void {
    this.sendJourneyEvent({
      id: uuid(),
      timestamp: this.getTimestamp(),
      journeyName: currentActiveJourneyName || '',
      eventType: { type: 'updateActiveJourney', activeJourneysData: activeJourneysJSONRepresentation },
      eventValue: currentActiveJourneyName || '',
      metadata,
    })
  }

  /**
   * Reports a user journey
   */
  didReportUserJourney(
    userJourneyName: string,
    requestJSONRepresentation: string,
    metadata?: Record<string, string>
  ): void {
    this.sendJourneyEvent({
      id: uuid(),
      timestamp: this.getTimestamp(),
      journeyName: userJourneyName,
      eventType: { type: 'report', requestData: requestJSONRepresentation },
      eventValue: '',
      metadata,
    })
  }

  /**
   * Adds logs to the journey timeline for debugging
   */
  log(message: string, metadata?: Record<string, string>): void {
    this.sendJourneyEvent({
      id: uuid(),
      timestamp: this.getTimestamp(),
      journeyName: '',
      eventType: { type: 'log' },
      eventValue: message,
      metadata,
    })
  }

  /**
   * Get current timestamp in milliseconds (Unix epoch)
   */
  private getTimestamp(): number {
    return Date.now()
  }
}
